import { Modal, Form, Input, Select, Checkbox } from 'antd';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { TextBlock, type IVariableFormatting } from '../types';
import {
  DEFAULT_FORMATTING,
  TEXT_BLOCK_OPTIONS,
  TEXT_BLOCK_FONT_SIZES,
} from '../utils';
import FormattingToolbar from '../components/FormattingToolbar';

export interface IProps {
  open: boolean;
  variablePath: string;
  initialFormatting?: IVariableFormatting;
  initialPersist?: boolean;
  onConfirm: (
    newPath: string,
    formatting: IVariableFormatting,
    persist: boolean
  ) => void;
  onCancel: () => void;
}

const calculatePreviewFontSize = (
  textBlock: TextBlock,
  fontSize?: string,
  isSuperscript?: boolean,
  isSubscript?: boolean
): string => {
  const baseSize = fontSize || TEXT_BLOCK_FONT_SIZES[textBlock] || '10.5pt';
  if (isSuperscript || isSubscript) {
    return `calc(${baseSize} * 0.75)`;
  }
  return baseSize;
};

const VariableEditModal: React.FC<IProps> = ({
  open,
  variablePath,
  initialFormatting,
  initialPersist,
  onConfirm,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [formatting, setFormatting] = useState<IVariableFormatting>(
    initialFormatting || DEFAULT_FORMATTING
  );
  const [persist, setPersist] = useState(initialPersist ?? false);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ variablePath });
      setFormatting(initialFormatting || DEFAULT_FORMATTING);
      setPersist(initialPersist ?? false);
    }
  }, [open, variablePath, initialFormatting, initialPersist, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onConfirm(values.variablePath, formatting, persist);
      form.resetFields();
      setFormatting(DEFAULT_FORMATTING);
      setPersist(false);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFormatting(DEFAULT_FORMATTING);
    setPersist(false);
    onCancel();
  };

  const currentPath = Form.useWatch('variablePath', form);

  return (
    <Modal
      title="Edit Variable"
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      okText="Save"
      cancelText="Cancel"
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Variable Path"
          name="variablePath"
          rules={[
            { required: true, message: 'Please enter a variable path' },
            // {
            //   pattern: /^\{\{(@\.)?[\w.]+\}\}$/,
            //   message:
            //     'Invalid format. Use {{@.path}} or {{itemName.path}} format',
            // },
          ]}
        >
          <Input.TextArea
            placeholder="e.g., {{@.mah.name}} or {{person.name}}"
            autoSize={{ minRows: 2, maxRows: 8 }}
            autoFocus
          />
        </Form.Item>

        <Form.Item>
          <Checkbox
            checked={persist}
            onChange={e => setPersist(e.target.checked)}
          >
            Persist when missing
          </Checkbox>
        </Form.Item>

        <Form.Item label="Text Block">
          <Select
            value={formatting.textBlock}
            onChange={value =>
              setFormatting(prev => ({ ...prev, textBlock: value }))
            }
            options={TEXT_BLOCK_OPTIONS}
            style={{ width: 150 }}
          />
        </Form.Item>

        <Form.Item label="Formatting">
          <FormattingToolbar
            formatting={formatting}
            onFormattingChange={setFormatting}
          />
        </Form.Item>

        <Form.Item label="Preview">
          <PreviewBox $alignment={formatting.alignment}>
            <PreviewText
              as={formatting.textBlock}
              $textBlock={formatting.textBlock}
              $bold={formatting.bold}
              $italic={formatting.italic}
              $underline={formatting.underline}
              $strikethrough={formatting.strikethrough}
              $superscript={formatting.superscript}
              $subscript={formatting.subscript}
              $fontFamily={formatting.fontFamily}
              $fontSize={formatting.fontSize}
            >
              {currentPath}
            </PreviewText>
          </PreviewBox>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default VariableEditModal;

const PreviewBox = styled.div<{ $alignment: string }>`
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.surface};
  text-align: ${({ $alignment }) => $alignment};
`;

const PreviewText = styled.span<{
  $textBlock: TextBlock;
  $bold: boolean;
  $italic: boolean;
  $underline: boolean;
  $strikethrough: boolean;
  $superscript: boolean;
  $subscript: boolean;
  $fontFamily?: string;
  $fontSize?: string;
}>`
  display: inline-block;
  font-size: ${({ $textBlock, $superscript, $subscript, $fontSize }) =>
    calculatePreviewFontSize($textBlock, $fontSize, $superscript, $subscript)};
  font-family: ${({ $fontFamily }) => $fontFamily || 'inherit'};
  font-weight: ${({ $bold, $textBlock }) =>
    $bold || $textBlock !== TextBlock.Paragraph ? 'bold' : 'normal'};
  font-style: ${({ $italic }) => ($italic ? 'italic' : 'normal')};
  text-decoration: ${({ $underline, $strikethrough }) => {
    const decorations = [];
    if ($underline) decorations.push('underline');
    if ($strikethrough) decorations.push('line-through');
    return decorations.length > 0 ? decorations.join(' ') : 'none';
  }};
  vertical-align: ${({ $superscript, $subscript }) =>
    $superscript ? 'super' : $subscript ? 'sub' : 'baseline'};
  color: ${({ theme }) => theme.colors.primary};
  background: rgba(92, 102, 247, 0.072);
  padding: 2px 6px;
  border-radius: 4px;
  margin: 0;
`;
